/**
 * A share's and an invite's secrets on the writer's device (part 38b log, DI1, DI2, DI9; principle 10): the owner secret
 * of a book's share, and for each invite its claim secret and person key, made at random; what derives from them (the
 * owner and claim proofs and the IDs, crypto's `shareOwnerProof`, `claimProof`, `shareIdOf`, `inviteIdOf`); the record's
 * IDs (the IDs' bytes in hex), checked against the secrets wherever a record is used (table BU: a forged record is never
 * used); and the invite link, whose fragment alone holds the claim secret and the person key. WebCrypto and the randomness
 * are injected.
 */
import { claimProof, INVITE_KEY_BYTES, INVITE_SECRET_BYTES, inviteIdOf, shareIdOf, shareOwnerProof } from '@cockatrice/crypto';
import { toHex } from '@cockatrice/core';
import type { InviteRecord, ShareRecord } from '@cockatrice/storage';
import { bytesOfLength, toBase64Url } from '../account/bytes.ts';
import { bytesOfHex } from './secrets.ts';
import type { Random } from './secrets.ts';

/** A share's secret and what derives from it. */
export interface ShareSecrets {
  readonly secret: Uint8Array<ArrayBuffer>;
  readonly proof: Uint8Array<ArrayBuffer>;
  /** The share's ID's bytes. */
  readonly id: Uint8Array<ArrayBuffer>;
  /** The share's ID as the relay names it: base64url. */
  readonly share: string;
}

/** An invite's secrets and what derives from them. */
export interface InviteSecrets {
  readonly claim: Uint8Array<ArrayBuffer>;
  readonly key: Uint8Array<ArrayBuffer>;
  readonly proof: Uint8Array<ArrayBuffer>;
  /** The invite's ID's bytes. */
  readonly id: Uint8Array<ArrayBuffer>;
  /** The invite's ID as the relay names it: base64url. */
  readonly invite: string;
}

/** DI2: what owner secret `secret` gives. */
async function shareOf(subtle: SubtleCrypto, secret: Uint8Array<ArrayBuffer>): Promise<ShareSecrets> {
  const proof = await shareOwnerProof(subtle, secret);
  const id = await shareIdOf(subtle, proof);
  return { secret, proof, id, share: toBase64Url(id) };
}

/** DI2: what claim secret `claim` and person key `key` give. */
async function inviteOf(subtle: SubtleCrypto, claim: Uint8Array<ArrayBuffer>, key: Uint8Array<ArrayBuffer>): Promise<InviteSecrets> {
  const proof = await claimProof(subtle, claim);
  const id = await inviteIdOf(subtle, proof);
  return { claim, key, proof, id, invite: toBase64Url(id) };
}

/** DI1: a new share's secrets: a fresh owner secret. */
export function newShareSecrets(subtle: SubtleCrypto, random: Random): Promise<ShareSecrets> {
  return shareOf(subtle, random(INVITE_SECRET_BYTES));
}

/** DI1, DI9: a new invite's secrets: a fresh claim secret and a fresh person key (only ever made for the writer's own invite). */
export function newInviteSecrets(subtle: SubtleCrypto, random: Random): Promise<InviteSecrets> {
  return inviteOf(subtle, random(INVITE_SECRET_BYTES), random(INVITE_KEY_BYTES));
}

/** Table BU: the secrets a share record holds, or null when its ID isn't its secret's own (a forged or broken record). */
export async function shareSecretsOf(subtle: SubtleCrypto, record: Pick<ShareRecord, 'id' | 'secret'>): Promise<ShareSecrets | null> {
  const secrets = await shareOf(subtle, bytesOfHex(record.secret));
  return toHex(secrets.id) === record.id ? secrets : null;
}

/** Table BU: the secrets an invite record holds, or null when its ID isn't its claim secret's own. */
export async function inviteSecretsOf(subtle: SubtleCrypto, record: Pick<InviteRecord, 'id' | 'claim' | 'key'>): Promise<InviteSecrets | null> {
  const secrets = await inviteOf(subtle, bytesOfHex(record.claim), bytesOfHex(record.key));
  return toHex(secrets.id) === record.id ? secrets : null;
}

/** The route an invite link opens in the app (part 38c reads it). */
export const INVITE_ROUTE = '#/invite/';

/**
 * DI2, IW12: the invite link at the app's `origin`: the app's invite route, then the claim secret and the person key in
 * base64url, joined by a dot. All of it sits in the fragment, which a browser never sends.
 */
export function inviteUrl(origin: string, secrets: Pick<InviteSecrets, 'claim' | 'key'>): string {
  return `${origin}/${INVITE_ROUTE}${toBase64Url(secrets.claim)}.${toBase64Url(secrets.key)}`;
}

/** What an invite link's fragment holds: the claim secret and the person key. */
export interface InviteFragment {
  readonly claim: Uint8Array<ArrayBuffer>;
  readonly key: Uint8Array<ArrayBuffer>;
}

/**
 * The secrets in an invite link's fragment `hash` (`location.hash`, as {@link inviteUrl} writes it), or null for
 * anything else: another route, a part missing or of another length, or base64url in any spelling but its one.
 */
export function readInviteFragment(hash: string): InviteFragment | null {
  if (!hash.startsWith(INVITE_ROUTE)) return null;
  const parts = hash.slice(INVITE_ROUTE.length).split('.');
  const [claimText, keyText] = parts;
  if (parts.length !== 2 || claimText === undefined || keyText === undefined) return null;
  const claim = bytesOfLength(claimText, INVITE_SECRET_BYTES);
  const key = bytesOfLength(keyText, INVITE_KEY_BYTES);
  // `bytesOfLength` reads base64url strictly: only the one spelling `toBase64Url` writes.
  if (claim === null || key === null) return null;
  return { claim, key };
}
